import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Phone, MapPin, Snowflake, PackageSearch, CalendarDays, Wrench } from 'lucide-react';
import useSEO from '../hooks/useSEO';
import { PartnerMapSection } from './PartnerLocations';

const LOCATIONS = {
    'raleigh':       { name: 'Raleigh, NC',       region: 'NC Triangle', industries: ['Manufacturing', 'Pharmaceuticals', 'Automotive', 'Food Processing'] },
    'durham':        { name: 'Durham, NC',        region: 'NC Triangle', industries: ['Biotech', 'Pharmaceuticals', 'Manufacturing', 'Data Centers'] },
    'cary':          { name: 'Cary, NC',          region: 'NC Triangle', industries: ['Technology', 'Light Manufacturing', 'R&D Facilities'] },
    'apex':          { name: 'Apex, NC',          region: 'NC Triangle', industries: ['Manufacturing', 'Distribution', 'Industrial Facilities'] },
    'greensboro':    { name: 'Greensboro, NC',    region: 'NC Triad',    industries: ['Aerospace', 'Automotive', 'Textiles', 'Logistics'] },
    'winston-salem': { name: 'Winston-Salem, NC', region: 'NC Triad',    industries: ['Manufacturing', 'Food & Beverage', 'Pharmaceuticals'] },
    'high-point':    { name: 'High Point, NC',    region: 'NC Triad',    industries: ['Furniture', 'Manufacturing', 'Warehousing'] },
    'charlotte':     { name: 'Charlotte, NC',     region: 'Western NC',  industries: ['Manufacturing', 'Aerospace', 'Financial Services', 'Distribution'] },
    'fayetteville':  { name: 'Fayetteville, NC',  region: 'Eastern NC',  industries: ['Manufacturing', 'Military & Defense', 'Food Processing'] },
    'wilmington':    { name: 'Wilmington, NC',    region: 'Coastal NC',  industries: ['Marine', 'Food Processing', 'Pharmaceuticals', 'Logistics'] },
    'asheville':     { name: 'Asheville, NC',     region: 'Western NC',  industries: ['Manufacturing', 'Brewing', 'Automotive', 'Aerospace'] },
    'greenville':    { name: 'Greenville, NC',    region: 'Eastern NC',  industries: ['Pharmaceuticals', 'Manufacturing', 'Agriculture'] },
};

const OFFERS = [
    { icon: Snowflake, name: 'Dry Ice Blasting Machines', to: '/shop?type=machine', desc: 'New and used dry ice blasters quoted with freight, setup and training.' },
    { icon: PackageSearch, name: 'Accessories & Parts', to: '/shop?type=accessory', desc: 'Nozzles, hoses and wear parts — order online and ship straight to your site.' },
    { icon: CalendarDays, name: 'Rental & Training', to: '/rental', desc: 'Rent by the day or week and get your operators certified.' },
    { icon: Wrench, name: 'Service & Support', to: '/services', desc: 'Repairs, maintenance and a support line that answers within 24 hours.' },
];

const Location = () => {
    const { city } = useParams();
    const loc = LOCATIONS[city];

    useSEO(loc ? {
        title: `Dry Ice Blasting Equipment in ${loc.name} — Machines, Parts, Rental & Training`,
        description: `Buy or rent dry ice blasting machines in ${loc.name}. Accessories and parts shipped fast, operator training, repairs and authorized partners across ${loc.region}.`,
    } : { title: 'Location Not Found' });

    if (!loc) {
        return (
            <div className="container-page pt-32 pb-24">
                <div className="label-caps text-brand-primary mb-4">/ 404</div>
                <h1 className="font-display font-black uppercase text-4xl text-neutral-900">Location not found</h1>
                <Link to="/partner-locations" className="mt-6 inline-flex items-center gap-2 label-caps text-brand-primary">
                    See all partner locations <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        );
    }

    return (
        <div className="pt-24 pb-24" data-testid="location-page">
            <div className="container-page">
                <div className="label-caps text-brand-primary mb-4 inline-flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> / {loc.region}</div>
                <h1 className="font-display font-black uppercase text-5xl md:text-7xl leading-[0.95] text-neutral-900">
                    Dry ice blasting<br />equipment in <span className="text-brand-primary">{loc.name}.</span>
                </h1>
                <p className="mt-6 max-w-3xl text-base md:text-lg text-neutral-600 leading-relaxed">
                    Dry Ice Bulls supplies operators and facilities in {loc.name} and the wider {loc.region} region with dry ice blasting machines,
                    accessories and parts — plus rentals, operator training and support from people who run the equipment every day.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/shop" className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-6 py-3.5 label-caps transition-colors" data-testid="location-cta-shop">
                        Shop equipment <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link to={`/contact?interest=quote&subject=${encodeURIComponent(`Equipment quote: ${loc.name}`)}`} className="inline-flex items-center gap-2 border border-neutral-300 hover:border-neutral-900 text-neutral-900 px-6 py-3.5 label-caps transition-colors" data-testid="location-cta-quote">
                        Get a machine quote
                    </Link>
                    <a href="tel:+19842010767" className="inline-flex items-center gap-2 border border-neutral-300 hover:border-neutral-900 text-neutral-900 px-6 py-3.5 label-caps transition-colors" data-testid="location-cta-call">
                        <Phone className="h-4 w-4" /> (984) 201-0767
                    </a>
                </div>

                <section className="mt-16">
                    <div className="label-caps text-brand-primary mb-4">/ For {loc.name} operators</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {OFFERS.map(({ icon: Icon, name, to, desc }) => (
                            <Link key={name} to={to} className="group border border-neutral-200 bg-white p-6 hover:border-brand-primary transition-colors flex gap-4" data-testid={`location-offer-${to.replace(/[^a-z]/g, '')}`}>
                                <div className="w-12 h-12 border border-neutral-200 flex items-center justify-center shrink-0 group-hover:border-brand-primary group-hover:bg-brand-primary/5">
                                    <Icon className="h-5 w-5 text-brand-primary" />
                                </div>
                                <div>
                                    <div className="font-display font-bold uppercase text-lg text-neutral-900 group-hover:text-brand-primary">{name}</div>
                                    <p className="mt-1 text-sm text-neutral-600 leading-relaxed">{desc}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>

                <section className="mt-16">
                    <div className="label-caps text-brand-primary mb-4">/ Industries we equip in {loc.name}</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {loc.industries.map((ind) => (
                            <div key={ind} className="border border-neutral-200 bg-white p-4"><div className="font-display font-bold uppercase text-base text-neutral-900">{ind}</div></div>
                        ))}
                    </div>
                </section>

                <section className="mt-16" data-testid="location-partner-map">
                    <div className="label-caps text-brand-primary mb-4">/ Dry Ice Bulls & partners near {loc.name}</div>
                    <PartnerMapSection />
                </section>
            </div>
        </div>
    );
};

export default Location;
